import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CalendarDays, Zap, X } from 'lucide-react';
import dayjs from 'dayjs';
import { getCustomerInvoices } from 'Containers/Settings/store/selectors';
import { sendMessageToApp } from 'utils/exsternalCall';
import { getPayableInvoice } from 'utils/subscriptionGate';
import styles from './SubscriptionRenewalBanner.module.scss';

const DISMISS_KEY = 'subscriptionRenewalBannerDismissed';

const getLocalToday = () => dayjs().format('YYYY-MM-DD');

const isDismissedToday = (scadenza) => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissed = JSON.parse(raw);
    if (!dismissed?.day || dismissed.day !== getLocalToday()) return false;
    if (
      scadenza != null &&
      dismissed.scadenza != null &&
      String(dismissed.scadenza) !== String(scadenza)
    ) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

const dismissForToday = (scadenza) => {
  localStorage.setItem(
    DISMISS_KEY,
    JSON.stringify({
      day: getLocalToday(),
      scadenza: scadenza != null ? String(scadenza) : null,
    }),
  );
};

/**
 * Avviso rinnovo in arrivo.
 * Stripe non emette la fattura prima della scadenza → CTA solo se c’è una fattura da pagare.
 * “Non mostrare” nasconde il banner fino a domani (stesso giorno locale).
 */
const SubscriptionRenewalBanner = ({ daysLeft, expiryDate }) => {
  const { t, i18n } = useTranslation();
  const invoices = useSelector(getCustomerInvoices());
  const fromApp = !!localStorage.getItem('fromApp');
  const [hidden, setHidden] = useState(() => isDismissedToday(expiryDate));

  const unpaidInvoiceUrl = useMemo(
    () => getPayableInvoice(invoices)?.hosted_invoice_url || null,
    [invoices],
  );

  const formattedDate = useMemo(() => {
    if (!expiryDate) return '';
    return dayjs(expiryDate).locale(i18n.language || 'it').format('D MMMM YYYY');
  }, [expiryDate, i18n.language]);

  const handlePay = () => {
    if (!unpaidInvoiceUrl) return;
    if (fromApp) {
      sendMessageToApp('app:url', { url: unpaidInvoiceUrl });
      return;
    }
    window.open(unpaidInvoiceUrl, '_blank');
  };

  const handleDismiss = () => {
    dismissForToday(expiryDate);
    setHidden(true);
  };

  if (hidden) return null;

  return (
    <aside className={styles.banner} role="status">
      <div className={styles.info}>
        <span className={styles.badge}>
          {formattedDate
            ? t('home.subscriptionRenewalBanner.dateChip', { date: formattedDate })
            : t('home.subscriptionRenewalBanner.badge')}
        </span>
        <div className={styles.title_row}>
          <span className={styles.icon} aria-hidden>
            <CalendarDays size={22} strokeWidth={2.2} />
          </span>
          <p className={styles.title}>
            {t('home.subscriptionRenewalBanner.title', { count: daysLeft })}
          </p>
        </div>
        <p className={styles.subtitle}>
          {unpaidInvoiceUrl
            ? t('home.subscriptionRenewalBanner.subtitlePay')
            : t('home.subscriptionRenewalBanner.subtitle')}
        </p>
      </div>
      <div className={styles.actions}>
        {unpaidInvoiceUrl ? (
          <button type="button" className={styles.cta} onClick={handlePay}>
            <Zap size={16} strokeWidth={2.4} aria-hidden />
            {t('home.subscriptionRenewalBanner.payCta')}
          </button>
        ) : null}
        <button type="button" className={styles.dismiss} onClick={handleDismiss}>
          <X size={14} strokeWidth={2.4} aria-hidden />
          {t('home.subscriptionRenewalBanner.dismiss')}
        </button>
      </div>
    </aside>
  );
};

export default SubscriptionRenewalBanner;
